import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import Button from '../components/Button';
import Modal from '../components/Modal';
import { organizerEventsService } from '../services/organizerEventsService';
import {
    AccessOverview,
    AccessRequestError,
    Collision,
    organizerAccessService,
    ScanMethod,
    ScannerDevice,
    ScanResult
} from '../services/organizerAccessService';
import { DATE_TIME_SHORT, formatEventDate, formatInstant } from '../utils/dates';

// The door moves fast while the event is running
const REFRESH_MS = 10_000;
// Phones sync every 5 s while their app is open: older than this, the phone is probably offline
const SYNC_STALE_MS = 60_000;

interface OrganizerEvent {
    id: string;
    name: string;
    startDate?: string;
    location?: string;
    status: 'ACTIVE' | 'DRAFT' | 'FINISHED' | 'CANCELLED' | 'SOLD_OUT';
}

const RESULTS: Record<ScanResult, { label: string; badge: string }> = {
    ACCEPTED: { label: 'Ingresó', badge: 'st-badge-success' },
    ALREADY_USED: { label: 'Ya había ingresado', badge: 'st-badge-error' },
    VOID: { label: 'Boleta anulada', badge: 'st-badge-error' },
    NOT_FOUND: { label: 'No válida', badge: 'st-badge-warning' },
    UNVERIFIED: { label: 'Sin verificar', badge: 'st-badge-warning' }
};

const METHODS: Record<ScanMethod, string> = { QR: 'QR', MANUAL: 'Código manual' };

const percent = (part: number, total: number) => total > 0 ? Math.min(100, Math.round((part / total) * 100)) : 0;

const formatNumber = (value: number) => new Intl.NumberFormat('es-CO').format(value);

// The event's start: a wall time, shown as the organizer typed it
const formatEventStart = (dateString?: string) => formatEventDate(dateString, DATE_TIME_SHORT, 'Fecha por definir');

// Instants (access end, pairing, scans), shown in Bogotá time
const formatDate = (dateString?: string) => formatInstant(dateString, DATE_TIME_SHORT, 'Fecha por definir');

const TIME_WITH_SECONDS: Intl.DateTimeFormatOptions = { hour: 'numeric', minute: '2-digit', second: '2-digit' };

const formatTime = (dateString?: string | Date) => formatInstant(dateString, TIME_WITH_SECONDS, '—');

const formatAgo = (dateString: string) => {
    const seconds = Math.max(0, Math.round((Date.now() - new Date(dateString).getTime()) / 1000));
    if (seconds < 60) return `hace ${seconds} s`;
    const minutes = Math.round(seconds / 60);
    return minutes < 60 ? `hace ${minutes} min` : `hace ${Math.round(minutes / 60)} h`;
};

// Whether the phone's entries are reaching the others
const SyncStatus = ({ device }: { device: ScannerDevice }) => {
    if (device.inactive) return <span className="text-[var(--st-text-muted)]">—</span>;
    if (!device.lastSyncAt) {
        return <span className="text-[var(--st-text-secondary)]" title="Este teléfono aún no tiene la lista de boletas: valida en línea">En línea</span>;
    }
    const stale = Date.now() - new Date(device.lastSyncAt).getTime() > SYNC_STALE_MS;
    return (
        <div>
            <span className={`st-badge ${stale ? 'st-badge-warning' : 'st-badge-success'} st-badge-dot text-xs`}>
                {stale ? 'Sin conexión' : 'Sincronizado'}
            </span>
            <span className="block text-xs text-[var(--st-text-muted)] mt-1">{formatAgo(device.lastSyncAt)}</span>
            {device.pendingScans > 0 && (
                <span className="block text-xs font-bold text-[var(--st-warning)]">{formatNumber(device.pendingScans)} por enviar</span>
            )}
        </div>
    );
};

const CollisionsCard = ({ collisions, total }: { collisions: Collision[]; total: number }) => (
    <div className="st-card-static overflow-hidden">
        <div className="p-5 border-b border-[var(--st-border)]">
            <h2 className="font-display text-lg font-bold text-[var(--st-text-primary)]">Ingresos duplicados sin conexión</h2>
            <p className="text-sm text-[var(--st-text-secondary)] mt-1">
                {formatNumber(total)} {total === 1 ? 'boleta entró' : 'boletas entraron'} más de una vez: dos puertas sin conexión dejaron
                entrar copias del mismo QR antes de sincronizarse. Cuenta el primer ingreso; revisa los demás con tu equipo.
            </p>
        </div>
        <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse text-sm">
                <thead>
                    <tr className="bg-[var(--st-bg-tertiary)] text-xs uppercase text-[var(--st-text-muted)] tracking-wider">
                        <th className="p-4 font-bold">Boleta</th>
                        <th className="p-4 font-bold">Ingresos</th>
                    </tr>
                </thead>
                <tbody>
                    {collisions.map((collision, index) => (
                        <tr key={`${collision.code}-${index}`} className="border-t border-[var(--st-border)] align-top">
                            <td className="p-4">
                                <span className="font-mono text-[var(--st-text-primary)]">{collision.code ?? '—'}</span>
                                {collision.stageName && <span className="block text-xs text-[var(--st-text-muted)]">{collision.stageName}</span>}
                            </td>
                            <td className="p-4 text-[var(--st-text-secondary)]">
                                {collision.entries.map((entry, entryIndex) => (
                                    <span key={entryIndex} className="block">
                                        {formatTime(entry.at)} · {entry.deviceName}
                                        {entryIndex === 0 && <span className="st-badge st-badge-neutral text-xs ml-2">Cuenta</span>}
                                        {entry.offline && <span className="text-xs text-[var(--st-text-muted)]"> (sin conexión)</span>}
                                    </span>
                                ))}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    </div>
);

const ProgressBar = ({ value }: { value: number }) => (
    <div className="w-full h-2 bg-[var(--st-bg-tertiary)] rounded-full overflow-hidden border border-[var(--st-border)]">
        <div className="h-full bg-[var(--st-accent-primary)] transition-all" style={{ width: `${value}%` }}></div>
    </div>
);

const SummaryCard = ({ label, value, hint }: { label: string; value: string; hint?: string }) => (
    <div className="st-card-static p-5">
        <p className="text-xs text-[var(--st-text-muted)] uppercase font-bold tracking-wider mb-1">{label}</p>
        <p className="font-display text-2xl font-bold text-[var(--st-text-primary)]">{value}</p>
        {hint && <p className="text-xs text-[var(--st-text-muted)] mt-1">{hint}</p>}
    </div>
);

// Without an event in the URL: the organizer picks one of the events that can have door validation
const EventPicker = () => {
    const navigate = useNavigate();
    const [events, setEvents] = useState<OrganizerEvent[] | null>(null);
    const [error, setError] = useState(false);

    const load = useCallback(async () => {
        setError(false);
        try {
            const data = await organizerEventsService.getMyEvents();
            setEvents((data.content || []).filter((event: OrganizerEvent) => event.status !== 'DRAFT' && event.status !== 'CANCELLED'));
        } catch {
            setError(true);
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    return (
        <div className="st-page py-12">
            <div className="st-container">
                <div className="mb-8">
                    <h1 className="font-display text-3xl font-bold text-[var(--st-text-primary)]">Control de acceso</h1>
                    <p className="text-[var(--st-text-secondary)] max-w-3xl">
                        Valida las boletas en la puerta con la app SuperTicket Scanner. Elige el evento para vincular los
                        teléfonos de tu equipo y seguir los ingresos en tiempo real.
                    </p>
                </div>

                {events === null && !error && (
                    <div className="flex items-center justify-center min-h-[30vh]"><div className="st-spinner st-spinner-xl"></div></div>
                )}

                {error && (
                    <div className="st-card-static p-8 text-center">
                        <p className="text-[var(--st-text-secondary)] mb-4">No pudimos cargar tus eventos.</p>
                        <Button className="st-btn st-btn-primary" onClick={load}>Intentar de Nuevo</Button>
                    </div>
                )}

                {events !== null && events.length === 0 && (
                    <div className="st-card-static p-10 text-center">
                        <h2 className="font-display text-xl font-bold text-[var(--st-text-primary)] mb-2">Aún no tienes eventos publicados</h2>
                        <p className="text-[var(--st-text-secondary)] mb-6">El control de acceso está disponible para los eventos publicados.</p>
                        <Button className="st-btn st-btn-primary" onClick={() => navigate('/organizer/events')}>Ir a Mis Eventos</Button>
                    </div>
                )}

                {events !== null && events.length > 0 && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {events.map(event => (
                            <button
                                key={event.id}
                                className="st-card p-5 text-left"
                                onClick={() => navigate(`/organizer/access/${event.id}`)}
                            >
                                <div className="flex items-center gap-3 flex-wrap mb-1">
                                    <h2 className="font-display text-lg font-bold text-[var(--st-text-primary)]">{event.name}</h2>
                                    {event.status === 'FINISHED'
                                        ? <span className="st-badge st-badge-neutral">Finalizado</span>
                                        : <span className="st-badge st-badge-success st-badge-dot">Activo</span>}
                                </div>
                                <p className="text-sm text-[var(--st-text-secondary)]">
                                    {formatEventStart(event.startDate)}{event.location ? ` · ${event.location}` : ''}
                                </p>
                            </button>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

const PairingCard = ({ overview, onCreateCode }: { overview: AccessOverview; onCreateCode: () => void }) => {
    if (!overview.accessOpen) {
        return (
            <div className="st-card-static p-6">
                <h2 className="font-display text-lg font-bold text-[var(--st-text-primary)] mb-2">Vincular teléfonos</h2>
                <p className="text-[var(--st-text-secondary)]">
                    El control de acceso de este evento ya terminó: no se pueden vincular más teléfonos y los vinculados
                    dejaron de validar boletas.
                </p>
            </div>
        );
    }

    const code = overview.pairingCode;
    return (
        <div className="st-card-static p-6">
            <h2 className="font-display text-lg font-bold text-[var(--st-text-primary)] mb-4">Vincular teléfonos</h2>
            <div className="flex flex-col md:flex-row gap-6 items-start">
                {code ? (
                    <div className="flex flex-col items-center gap-2 mx-auto md:mx-0">
                        <img src={code.qrImage} alt={`QR de vinculación ${code.code}`} className="w-48 h-48 border border-[var(--st-border)] rounded-xl" />
                        <p className="font-mono text-2xl font-bold tracking-widest text-[var(--st-text-primary)]">{code.code}</p>
                    </div>
                ) : null}
                <div className="flex-1">
                    <ol className="list-decimal pl-5 space-y-2 text-[var(--st-text-secondary)] mb-4">
                        <li>Instala <span className="font-bold">SuperTicket Scanner</span> en el teléfono Android de cada persona de tu equipo en la puerta.</li>
                        <li>Abre la app y escanea este QR, o escribe el código si la cámara no lo lee.</li>
                        <li>Ponle un nombre al teléfono (por ejemplo, "Puerta norte") para saber por dónde entra cada persona.</li>
                    </ol>
                    <p className="text-sm text-[var(--st-text-muted)] mb-4">
                        Los teléfonos validan boletas hasta el {formatDate(overview.accessEnd)}. Comparte el código solo con tu
                        equipo: quien lo tenga puede vincular un teléfono mientras esté vigente.
                    </p>
                    <Button variant={code ? 'secondary' : 'primary'} className={`st-btn ${code ? '' : 'st-btn-primary'} st-btn-md`} onClick={onCreateCode}>
                        {code ? 'Generar nuevo código' : 'Generar código de vinculación'}
                    </Button>
                </div>
            </div>
        </div>
    );
};

const DevicesCard = ({ devices, onRevoke }: { devices: ScannerDevice[]; onRevoke: (device: ScannerDevice) => void }) => (
    <div className="st-card-static overflow-hidden">
        <div className="p-5 border-b border-[var(--st-border)]">
            <h2 className="font-display text-lg font-bold text-[var(--st-text-primary)]">Teléfonos vinculados</h2>
        </div>
        {devices.length === 0 ? (
            <p className="p-5 text-sm text-[var(--st-text-muted)]">Aún no hay teléfonos vinculados a este evento.</p>
        ) : (
            <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse text-sm">
                    <thead>
                        <tr className="bg-[var(--st-bg-tertiary)] text-xs uppercase text-[var(--st-text-muted)] tracking-wider">
                            <th className="p-4 font-bold">Teléfono</th>
                            <th className="p-4 font-bold">Sincronización</th>
                            <th className="p-4 font-bold text-right">Escaneos</th>
                            <th className="p-4 font-bold text-right">Ingresos</th>
                            <th className="p-4 font-bold text-right"></th>
                        </tr>
                    </thead>
                    <tbody>
                        {devices.map(device => (
                            <tr key={device.deviceId} className="border-t border-[var(--st-border)]">
                                <td className="p-4">
                                    <span className="font-medium text-[var(--st-text-primary)]">{device.name}</span>
                                    {device.inactive && <span className="st-badge st-badge-neutral text-xs ml-2">Desvinculado</span>}
                                    <span className="block text-xs text-[var(--st-text-muted)]">Vinculado: {formatDate(device.pairedAt)}</span>
                                </td>
                                <td className="p-4"><SyncStatus device={device} /></td>
                                <td className="p-4 text-right text-[var(--st-text-secondary)]">{formatNumber(device.scans)}</td>
                                <td className="p-4 text-right font-bold text-[var(--st-text-primary)]">{formatNumber(device.admitted)}</td>
                                <td className="p-4 text-right">
                                    {!device.inactive && (
                                        <Button variant="ghost" className="st-btn st-btn-ghost st-btn-sm" onClick={() => onRevoke(device)}>
                                            Desvincular
                                        </Button>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        )}
    </div>
);

const AccessPanel = ({ eventId }: { eventId: string }) => {
    const [overview, setOverview] = useState<AccessOverview | null>(null);
    const [loadError, setLoadError] = useState<'not-found' | 'unavailable' | null>(null);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [updatedAt, setUpdatedAt] = useState<Date | null>(null);
    const [confirmNewCode, setConfirmNewCode] = useState(false);
    const [revoking, setRevoking] = useState<ScannerDevice | null>(null);
    const [isWorking, setIsWorking] = useState(false);
    const [notice, setNotice] = useState<{ kind: 'success' | 'error'; text: string } | null>(null);
    const refreshTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

    const load = useCallback(async () => {
        if (refreshTimer.current) clearTimeout(refreshTimer.current);
        setIsRefreshing(true);
        let keepRefreshing = true;
        try {
            setOverview(await organizerAccessService.getAccess(eventId));
            setUpdatedAt(new Date());
            setLoadError(null);
        } catch (e) {
            const notFound = e instanceof AccessRequestError && e.status === 404;
            keepRefreshing = !notFound;
            // Keeps the last data on screen: the next refresh may work
            setLoadError(notFound ? 'not-found' : 'unavailable');
        } finally {
            setIsRefreshing(false);
            // Only while the tab is visible; it refreshes as soon as it becomes visible again
            if (keepRefreshing && document.visibilityState === 'visible') {
                refreshTimer.current = setTimeout(load, REFRESH_MS);
            }
        }
    }, [eventId]);

    useEffect(() => {
        setOverview(null);
        load();
        const onVisibilityChange = () => {
            if (document.visibilityState === 'visible') load();
        };
        document.addEventListener('visibilitychange', onVisibilityChange);
        return () => {
            document.removeEventListener('visibilitychange', onVisibilityChange);
            if (refreshTimer.current) clearTimeout(refreshTimer.current);
        };
    }, [load]);

    const createCode = async () => {
        setIsWorking(true);
        try {
            const pairingCode = await organizerAccessService.createPairingCode(eventId);
            setOverview(current => current ? { ...current, pairingCode } : current);
            setNotice(null);
        } catch (e) {
            const closed = e instanceof AccessRequestError && e.code === 'ACCESS_CLOSED';
            setNotice({
                kind: 'error',
                text: closed
                    ? 'El control de acceso de este evento ya terminó.'
                    : 'No pudimos generar el código. Intenta de nuevo en unos segundos.'
            });
            if (closed) load();
        } finally {
            setIsWorking(false);
            setConfirmNewCode(false);
        }
    };

    const revoke = async () => {
        if (!revoking) return;
        setIsWorking(true);
        try {
            await organizerAccessService.revokeDevice(revoking.deviceId);
            setNotice({ kind: 'success', text: `"${revoking.name}" quedó desvinculado y ya no puede validar boletas.` });
            load();
        } catch {
            setNotice({ kind: 'error', text: 'No pudimos desvincular el teléfono. Intenta de nuevo.' });
        } finally {
            setIsWorking(false);
            setRevoking(null);
        }
    };

    if (overview === null) {
        return (
            <div className="st-page py-12">
                <div className="st-container">
                    {loadError ? (
                        <div className="st-card-static p-8 text-center">
                            <h1 className="font-display text-2xl font-bold text-[var(--st-text-primary)] mb-2">
                                {loadError === 'not-found' ? 'No encontramos este evento' : 'No pudimos cargar el control de acceso'}
                            </h1>
                            <p className="text-[var(--st-text-secondary)] mb-6">
                                {loadError === 'not-found'
                                    ? 'Puede que no sea uno de tus eventos o que aún no esté publicado.'
                                    : 'Intenta de nuevo en unos segundos.'}
                            </p>
                            <div className="flex gap-3 justify-center">
                                <Link to="/organizer/access" className="st-btn st-btn-ghost">Elegir otro evento</Link>
                                {loadError === 'unavailable' && (
                                    <Button className="st-btn st-btn-primary" onClick={load} disabled={isRefreshing}>
                                        {isRefreshing ? 'Consultando...' : 'Intentar de Nuevo'}
                                    </Button>
                                )}
                            </div>
                        </div>
                    ) : (
                        <div className="flex items-center justify-center min-h-[40vh]"><div className="st-spinner st-spinner-xl"></div></div>
                    )}
                </div>
            </div>
        );
    }

    const enteredPercent = percent(overview.entered, overview.issued);

    return (
        <div className="st-page py-12">
            <div className="st-container">

                {/* HEADER */}
                <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-8">
                    <div>
                        <Link to="/organizer/access" className="text-sm text-[var(--st-accent-primary)] font-medium">← Otros eventos</Link>
                        <h1 className="font-display text-3xl font-bold text-[var(--st-text-primary)] mt-1">Control de acceso</h1>
                        <p className="text-[var(--st-text-secondary)]">
                            <span className="font-bold text-[var(--st-text-primary)]">{overview.event.name}</span>
                            {' · '}{formatEventStart(overview.event.startDate)}{overview.event.location ? ` · ${overview.event.location}` : ''}
                        </p>
                    </div>
                    <div className="flex items-center gap-3">
                        {updatedAt && (
                            <span className="text-xs text-[var(--st-text-muted)]">
                                Actualizado a las {formatTime(updatedAt)}
                            </span>
                        )}
                        <Button variant="secondary" className="st-btn st-btn-md" onClick={load} disabled={isRefreshing}>
                            {isRefreshing ? 'Actualizando...' : 'Actualizar'}
                        </Button>
                    </div>
                </div>

                {loadError && (
                    <div className="st-alert st-alert-warning mb-6">
                        <div className="st-alert-content">
                            <p className="st-alert-message">
                                No pudimos actualizar los ingresos. Mostramos los últimos datos; volveremos a intentarlo en unos segundos.
                            </p>
                        </div>
                    </div>
                )}

                {notice && (
                    <div className={`st-alert ${notice.kind === 'success' ? 'st-alert-success' : 'st-alert-error'} mb-6`}>
                        <div className="st-alert-content"><p className="st-alert-message">{notice.text}</p></div>
                    </div>
                )}

                {/* RESUMEN */}
                <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
                    <SummaryCard label="Ingresaron" value={formatNumber(overview.entered)}
                                 hint={`${enteredPercent}% de ${formatNumber(overview.issued)} boletas`} />
                    <SummaryCard label="Por ingresar" value={formatNumber(Math.max(0, overview.issued - overview.entered))} />
                    <SummaryCard label="Intentos repetidos" value={formatNumber(overview.duplicates)}
                                 hint="Boletas que ya habían ingresado" />
                    <SummaryCard label="No válidas" value={formatNumber(overview.notFound)}
                                 hint="Códigos que no son de este evento" />
                    <SummaryCard label="Sin verificar" value={formatNumber(overview.unverified)}
                                 hint="Sin conexión y fuera de la lista del teléfono: no entraron" />
                </div>

                <div className="space-y-6">
                    <PairingCard
                        overview={overview}
                        onCreateCode={() => overview.pairingCode ? setConfirmNewCode(true) : createCode()}
                    />

                    {/* INGRESOS POR ETAPA */}
                    <div className="st-card-static overflow-hidden">
                        <div className="p-5 border-b border-[var(--st-border)]">
                            <h2 className="font-display text-lg font-bold text-[var(--st-text-primary)]">Ingresos por etapa</h2>
                        </div>
                        {overview.stages.length === 0 ? (
                            <p className="p-5 text-sm text-[var(--st-text-muted)]">Este evento aún no tiene boletas emitidas.</p>
                        ) : (
                            <div className="overflow-x-auto">
                                <table className="w-full text-left border-collapse text-sm">
                                    <thead>
                                        <tr className="bg-[var(--st-bg-tertiary)] text-xs uppercase text-[var(--st-text-muted)] tracking-wider">
                                            <th className="p-4 font-bold">Etapa</th>
                                            <th className="p-4 font-bold text-right">Boletas</th>
                                            <th className="p-4 font-bold text-right">Ingresaron</th>
                                            <th className="p-4 font-bold w-40">% ingresó</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {overview.stages.map(stage => {
                                            const stagePercent = percent(stage.entered, stage.issued);
                                            return (
                                                <tr key={stage.stageName} className="border-t border-[var(--st-border)]">
                                                    <td className="p-4 font-medium text-[var(--st-text-primary)]">{stage.stageName}</td>
                                                    <td className="p-4 text-right text-[var(--st-text-secondary)]">{formatNumber(stage.issued)}</td>
                                                    <td className="p-4 text-right font-bold text-[var(--st-text-primary)]">{formatNumber(stage.entered)}</td>
                                                    <td className="p-4">
                                                        <div className="flex items-center gap-2">
                                                            <ProgressBar value={stagePercent} />
                                                            <span className="text-xs font-bold text-[var(--st-text-secondary)] w-9 text-right">{stagePercent}%</span>
                                                        </div>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>

                    {overview.collisionCount > 0 && <CollisionsCard collisions={overview.collisions} total={overview.collisionCount} />}

                    <DevicesCard devices={overview.devices} onRevoke={setRevoking} />

                    {/* ÚLTIMOS ESCANEOS */}
                    <div className="st-card-static overflow-hidden">
                        <div className="p-5 border-b border-[var(--st-border)]">
                            <h2 className="font-display text-lg font-bold text-[var(--st-text-primary)]">Últimos escaneos</h2>
                        </div>
                        {overview.recentScans.length === 0 ? (
                            <p className="p-5 text-sm text-[var(--st-text-muted)]">Aún no se ha escaneado ninguna boleta.</p>
                        ) : (
                            <div className="overflow-x-auto">
                                <table className="w-full text-left border-collapse text-sm">
                                    <thead>
                                        <tr className="bg-[var(--st-bg-tertiary)] text-xs uppercase text-[var(--st-text-muted)] tracking-wider">
                                            <th className="p-4 font-bold">Hora</th>
                                            <th className="p-4 font-bold">Resultado</th>
                                            <th className="p-4 font-bold">Boleta</th>
                                            <th className="p-4 font-bold">Teléfono</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {overview.recentScans.map((scan, index) => (
                                            <tr key={`${scan.at}-${index}`} className="border-t border-[var(--st-border)]">
                                                <td className="p-4 text-[var(--st-text-secondary)] whitespace-nowrap">{formatTime(scan.at)}</td>
                                                <td className="p-4">
                                                    <span className={`st-badge ${RESULTS[scan.result].badge} text-xs`}>{RESULTS[scan.result].label}</span>
                                                </td>
                                                <td className="p-4 text-[var(--st-text-secondary)]">
                                                    {scan.code ? <span className="font-mono">{scan.code}</span> : '—'}
                                                    {scan.stageName && <span className="block text-xs text-[var(--st-text-muted)]">{scan.stageName}</span>}
                                                </td>
                                                <td className="p-4 text-[var(--st-text-secondary)]">
                                                    {scan.deviceName}
                                                    <span className="block text-xs text-[var(--st-text-muted)]">
                                                        {METHODS[scan.method]}{scan.offline ? ' · sin conexión' : ''}
                                                    </span>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <Modal
                isOpen={confirmNewCode}
                onClose={() => !isWorking && setConfirmNewCode(false)}
                title="Generar nuevo código"
                className="max-w-md"
            >
                <p className="text-[var(--st-text-secondary)] mb-6">
                    El código actual dejará de funcionar para vincular teléfonos nuevos. Los teléfonos ya vinculados siguen
                    validando boletas. Úsalo si el código llegó a alguien que no es de tu equipo.
                </p>
                <div className="flex flex-col sm:flex-row gap-3 justify-end">
                    <Button variant="ghost" className="st-btn st-btn-ghost" onClick={() => setConfirmNewCode(false)} disabled={isWorking}>
                        Cancelar
                    </Button>
                    <Button className="st-btn st-btn-primary" onClick={createCode} disabled={isWorking}>
                        {isWorking ? 'Generando...' : 'Generar'}
                    </Button>
                </div>
            </Modal>

            <Modal
                isOpen={revoking !== null}
                onClose={() => !isWorking && setRevoking(null)}
                title="Desvincular teléfono"
                className="max-w-md"
            >
                <p className="text-[var(--st-text-secondary)] mb-6">
                    "{revoking?.name}" dejará de validar boletas de inmediato. Sus ingresos registrados se conservan. Para
                    volver a usarlo habrá que vincularlo con el código.
                </p>
                <div className="flex flex-col sm:flex-row gap-3 justify-end">
                    <Button variant="ghost" className="st-btn st-btn-ghost" onClick={() => setRevoking(null)} disabled={isWorking}>
                        Cancelar
                    </Button>
                    <Button className="st-btn st-btn-primary" onClick={revoke} disabled={isWorking}>
                        {isWorking ? 'Desvinculando...' : 'Desvincular'}
                    </Button>
                </div>
            </Modal>
        </div>
    );
};

export default function OrganizerAccessControl() {
    const { eventId } = useParams<{ eventId: string }>();
    return eventId ? <AccessPanel eventId={eventId} /> : <EventPicker />;
}
